import { GAP } from "../panels.ts";

// The bridge (docs/design 4.10): a panel is its source card unfolded outward. The source card
// keeps an aura while its panel is open, and a 44px band of card surface leaves the card's right
// edge at its head row, crosses the gutter and opens the panel's (accent) left border where it
// enters, so card outline, band and panel outline read as one shape. When the source scrolls out
// of its column the band slides to the panel's top or bottom edge as an ↑/↓ stub; next to a spine
// it is a short bridge at the top; a spine keeps a parked stub (CSS). All reads first, then writes,
// once per animation frame.

const BAND = 44;
/** the panel's left border while it is linked (panels.css: 2px accent) */
const BORDER = 2;

/** The element that scrolls column `column` (the board column itself; a panel's .pscroll). */
export function scrollerOf(row: HTMLElement | null, column: number): HTMLElement | null {
  const col = row?.querySelector<HTMLElement>(`:scope > [data-col="${column}"]`) ?? null;
  return !col || column === 0 ? col : col.querySelector<HTMLElement>(":scope > .pscroll");
}

/** Where a column's visible part starts: under whatever sticks at its top (the board's header, a panel's head). */
export function visibleTop(scroller: HTMLElement, column: number): number {
  const top = scroller.getBoundingClientRect().top;
  const head = scroller.querySelector<HTMLElement>(column === 0 ? ":scope > .c1-stick, :scope > .onerow-wrap" : ":scope > .sp-sticky");
  const r = head?.getBoundingClientRect();
  return r && r.height && r.top <= top + 1 ? Math.max(top, r.bottom) : top;
}

export interface Found {
  /** the top-level card (it gets the aura) */
  card: HTMLElement;
  /** what the band leaves from: the card, or the row on it */
  shape: HTMLElement;
  isRow: boolean;
}

/**
 * Find a source in a column: item `num`'s own card there, or the row for it on the card it sits on
 * (`via`, else its parent, grandparent...). Top-level cards are marked by the slot after them.
 */
export function locate(
  row: HTMLElement,
  column: number,
  num: number,
  via: number | undefined,
  parentOf: (n: number) => number | null,
  /** a notes panel: the notes row on the card (when it shows), else the card */
  part?: "notes",
): Found | null {
  const root = scrollerOf(row, column);
  if (!root) return null;
  const cardOf = (n: number) => {
    const c = root.querySelector(`[data-card="${n}"]`)?.previousElementSibling;
    return c instanceof HTMLElement ? c : null;
  };
  const inside = (card: HTMLElement): Found => {
    const r = card.querySelector<HTMLElement>(`li[data-num="${num}"] > .sub-row, [data-num="${num}"]:not(.card)`);
    return r ? { card, shape: r, isRow: true } : { card, shape: card, isRow: false };
  };
  if (via != null) {
    const host = cardOf(via);
    if (host) return inside(host);
  }
  const own = cardOf(num);
  if (own && part === "notes") {
    // the band leaves from the card's notes row, outlined like a sub-item row
    const head = own.querySelector<HTMLElement>(":scope > .card-body > .notes-block > .notes-head");
    if (head) return { card: own, shape: head, isRow: true };
  }
  if (own) return { card: own, shape: own, isRow: false };
  let host = via ?? parentOf(num);
  for (let i = 0; host != null && i < 4; i++) {
    const card = cardOf(host);
    if (card) return inside(card);
    host = parentOf(host);
  }
  // an item's panel has one card: a reference in it (its place line, a note) came from that card
  if (root.closest<HTMLElement>(".pcol")?.dataset.kind !== "item") return null;
  const slots = root.querySelectorAll("[data-card]");
  const only = slots.length === 1 ? slots[0].previousElementSibling : null;
  return only instanceof HTMLElement ? { card: only, shape: only, isRow: false } : null;
}

/** Scroll a source into its column's view: first back to where it was on screen (`top`), then into view if it isn't. */
export function revealIn(row: HTMLElement, column: number, num: number, via: number | undefined, top: number | null, parentOf: (n: number) => number | null, part?: "notes") {
  const found = locate(row, column, num, via, parentOf, part);
  const scroller = scrollerOf(row, column);
  if (!found || !scroller) return;
  const el = found.shape;
  if (top != null) scroller.scrollTop += el.getBoundingClientRect().top - top;
  const vTop = visibleTop(scroller, column);
  const vBot = scroller.getBoundingClientRect().bottom;
  const r = el.getBoundingClientRect();
  if (r.top < vTop + 2 || r.top + Math.min(r.height, BAND + 16) > vBot - 2) scroller.scrollTop += r.top - (vTop + 12);
}

/** Bring a source to the top of its column's view, smoothly. */
export function scrollToShow(row: HTMLElement, column: number, el: HTMLElement) {
  const scroller = scrollerOf(row, column);
  if (!scroller) return el.scrollIntoView({ block: "center", behavior: "smooth" });
  scroller.scrollBy({ top: el.getBoundingClientRect().top - (visibleTop(scroller, column) + 12), behavior: "smooth" });
}

export interface BridgeState {
  panels: { id: number; source: { column: number; num: number; via?: number; label: string; part?: "notes" } | null }[];
  folded: Set<number>;
  phone: boolean;
  parentOf: (n: number) => number | null;
}

interface Plan {
  pcol: HTMLElement;
  bridge: HTMLElement | null;
  linked: boolean;
  show: boolean;
  dir?: "up" | "down" | "spine";
  vertical?: boolean;
  isRow?: boolean;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

/** The label turns on its side when the crossing is too short for it (9.5px mono: under 6px a character). */
const labelWidth = (s: string) => s.length * 5.8 + 4;

export function placeBridges(row: HTMLElement, s: BridgeState) {
  const plans: Plan[] = [];
  const auras = new Set<HTMLElement>();
  const rows = new Set<HTMLElement>();
  // -- reads
  s.panels.forEach((p, i) => {
    const pcol = row.querySelector<HTMLElement>(`:scope > [data-col="${i + 1}"]`);
    if (!pcol) return;
    const plan: Plan = { pcol, bridge: pcol.querySelector<HTMLElement>(":scope > .bridge"), linked: false, show: false };
    plans.push(plan);
    const src = p.source;
    if (s.phone || !src || src.column !== i) return;
    const spine = s.folded.has(p.id);
    const cr = pcol.getBoundingClientRect();
    if (i > 0 && s.folded.has(s.panels[i - 1].id)) {
      // the source column is folded: a short bridge at the top joins this panel to that spine
      plan.linked = true;
      if (!spine) Object.assign(plan, { show: true, dir: "spine", left: -(GAP + 1) - BORDER, top: 10, width: GAP + 1 + BORDER });
      return;
    }
    const found = locate(row, src.column, src.num, src.via, s.parentOf, src.part);
    const scroller = scrollerOf(row, src.column);
    if (!found || !scroller) return;
    auras.add(found.card);
    if (found.isRow) rows.add(found.shape);
    plan.linked = true;
    if (spine) return; // a spine keeps only its parked stub
    const r = found.shape.getBoundingClientRect();
    const vTop = visibleTop(scroller, src.column);
    const vBot = scroller.getBoundingClientRect().bottom;
    // from the inner edge of the card's outline (a row's: 2px) to through the panel's left border
    const x0 = r.right + (found.isRow ? 2 : 1);
    const x1 = cr.left + BORDER;
    let y0: number;
    let h: number;
    if (found.isRow) {
      y0 = r.top - 4;
      h = Math.min(BAND, r.height + 8);
    } else {
      // a card: at its head row; slides down to stay on the card while its head is under the sticky header
      y0 = r.top + 8;
      h = BAND;
      if (y0 < vTop + 2 && vTop + 2 + h <= r.bottom - 6) y0 = vTop + 2;
    }
    const above = y0 < vTop + 2;
    const below = y0 + h > vBot - 2;
    plan.show = true;
    if (above || below || x1 - x0 < 6) {
      // parked: an arrow stub in the gutter at the panel's top or bottom edge, still open into the panel
      const w = GAP - 4 + BORDER;
      Object.assign(plan, { dir: below && !above ? "down" : "up", left: -w, width: w, top: 10 });
      return;
    }
    Object.assign(plan, { isRow: found.isRow, left: x0 - cr.left - BORDER, top: y0 - cr.top, width: x1 - x0, height: h, vertical: x1 - x0 < labelWidth(src.label) });
  });
  // -- writes
  for (const [attr, set] of [
    ["source", auras],
    ["sourceRow", rows],
  ] as const) {
    row.querySelectorAll<HTMLElement>(attr === "source" ? "[data-source]" : "[data-source-row]").forEach((el) => !set.has(el) && delete el.dataset[attr]);
    set.forEach((el) => el.dataset[attr] !== "1" && (el.dataset[attr] = "1"));
  }
  for (const p of plans) {
    if (p.linked) p.pcol.dataset.linked = "1";
    else delete p.pcol.dataset.linked;
    const b = p.bridge;
    if (!b) continue;
    if (!p.show) {
      delete b.dataset.show;
      continue;
    }
    b.dataset.show = "1";
    if (p.dir) b.dataset.dir = p.dir;
    else delete b.dataset.dir;
    if (p.vertical) b.dataset.v = "1";
    else delete b.dataset.v;
    if (p.isRow) b.dataset.row = "1";
    else delete b.dataset.row;
    b.style.left = `${Math.round(p.left ?? 0)}px`;
    b.style.width = `${Math.round(p.width ?? 0)}px`;
    if (p.dir === "down") {
      b.style.top = "";
      b.style.bottom = "10px";
    } else {
      b.style.top = `${Math.round(p.top ?? 0)}px`;
      b.style.bottom = "";
    }
    b.style.height = p.height != null ? `${Math.round(p.height)}px` : "";
  }
}
